<script lang="ts">
  import '../app.css';
  import { onMount, untrack } from 'svelte';
  import { afterNavigate, goto } from '$app/navigation';
  import { page } from '$app/state';
  import { auth } from '$lib/auth/auth.svelte';
  import { sessionStore } from '$lib/api/session.svelte';
  import { localeStore } from '$lib/i18n-locale-store.svelte';
  import {
    deLocalizeUrl,
    extractLocaleFromUrl,
    getLocale,
    localizeHref,
    shouldRedirect
  } from '$lib/paraglide/runtime';
  import UserMenu from '$lib/ui/UserMenu.svelte';

  const { children } = $props();

  // Routes accessibles sans authentification (chemins de-localises).
  const PUBLIC_ROUTES = ['/home', '/auth/callback'];

  // Routes ou le UserMenu top-right est masque pour ne pas se superposer aux HUD existants.
  // /[map] (demo 3D) a son propre HUD avec des boutons en haut a droite ; le UserMenu reapparaitra
  // proprement integre au HUD quand on retravaillera la 3D pour V2 (cf TODO.md).
  const ROUTES_WITHOUT_USERMENU = ['/office', '/hotel', '/clinic', '/airport_terminal', '/triple_H', '/battle_royale', '/campus'];

  /**
   * Synchronise l'URL avec la locale detectee : si l'utilisateur arrive sur / ou /office sans
   * prefixe et que sa langue navigateur est fr, Paraglide redirige vers /fr/office.
   * Sans cela, on resterait sur une URL sans locale et getLocale() retournerait baseLocale.
   */
  async function syncLocaleUrl(url: string) {
    // Skip sur /auth/callback : un redirect Paraglide y declencherait un changement de locale,
    // donc un re-mount du subtree par {#key currentLocale}, donc une 2e run de handleCallback,
    // qui echouerait avec "PKCE artifacts missing" (consume-once). La page callback est invisible
    // (redirect immediat), tant pis si elle s'affiche brievement en baseLocale.
    if (new URL(url).pathname.endsWith('/auth/callback')) return;

    const decision = await shouldRedirect({ url });
    if (decision.shouldRedirect && decision.redirectUrl) {
      if (decision.redirectUrl.origin !== window.location.origin) {
        window.location.href = decision.redirectUrl.href;
        return;
      }
      await goto(decision.redirectUrl, { invalidateAll: true });
    }
  }

  onMount(() => {
    auth.restore();
    void syncLocaleUrl(window.location.href);
  });

  afterNavigate((navigation) => {
    if (navigation.to) {
      // Mise a jour du <html lang> apres chaque navigation : accessibilite + SEO.
      document.documentElement.lang = getLocale();
      // Sync du mirror reactif de la locale : alimente les composants 3D dans le <Canvas>
      // (qui sont hors du {#key currentLocale} des pages) pour qu'ils puissent re-peindre
      // leurs textures canvas i18n au switch de langue. Cf RobotUnlockButton.
      localeStore.sync();
      void syncLocaleUrl(navigation.to.url.href);
    }
  });

  // Locale extraite de l'URL en cours (reactif : page.url est un $state SvelteKit).
  // Sert de cle pour le {#key} qui n'enrobe QUE le UserMenu cote layout ; les pages enfants
  // appliquent leur propre {#key currentLocale} localement (cf welcome/home/callback/[map]).
  //
  // Historique : avant 2026-05-31 le {#key} enrobait {@render children()} au niveau layout,
  // ce qui forcait un re-mount complet de l'arbre Svelte au switch de langue. Sur les pages
  // HTML simples (welcome) c'etait invisible (~5-50ms), mais sur /[map] le re-mount detruisait
  // le contexte WebGL du <Canvas> Threlte, re-chargeait tous les .glb (Draco + KTX2 decode)
  // et reconstruisait la scene 3D entiere -> 1-2s de "refresh" visible. Le Canvas n'a pourtant
  // aucun texte i18n (RobotLabel dessine du contenu backend). Solution : isoler le {#key} en
  // local sur chaque page, /[map] ne wrappe que son HUD + panels et laisse le Canvas vivant.
  const currentLocale = $derived(extractLocaleFromUrl(page.url.href));

  $effect(() => {
    // Tant que auth.restore() n'a pas fini, on ne sait pas si l'utilisateur a une session valide.
    // Sans cette garde, un refresh sur /welcome fait : (1) auth=false [pas encore restaure],
    // (2) redirect /home?returnTo=/welcome, (3) restore auth'd, (4) /home auth'd -> /office, soit
    // /welcome court-circuite. Avec la garde, le redirect attend la verite stable de auth.
    if (!auth.initialized) return;
    // page.url.pathname est l'URL en barre d'adresse (localisee : /fr/home).
    // PUBLIC_ROUTES utilise les chemins de-localises ; on dois donc deLocalize avant comparaison,
    // sinon /fr/home ne matche jamais /home -> boucle de redirection.
    const path = deLocalizeUrl(page.url).pathname;

    // Redirector racine : / -> /welcome (auth) ou /home (anon). Centraliser ici evite le double
    // goto concurrent qu'on avait avec un onMount dans +page.svelte (= flash visible au logout :
    // /fr/home?returnTo=/welcome -> / -> /fr/home?returnTo=/welcome).
    if (path === '/') {
      goto(localizeHref(auth.isAuthenticated ? '/welcome' : '/home'), { replaceState: true });
      return;
    }

    const isPublic = PUBLIC_ROUTES.includes(path);
    if (!auth.isAuthenticated && !isPublic) {
      // localizeHref reapplique le prefixe pour l'URL visible en barre d'adresse.
      goto(localizeHref(`/home?returnTo=${encodeURIComponent(path)}`), { replaceState: true });
    }
    if (auth.isAuthenticated && path === '/home') {
      // /home authentifie -> /welcome (page d'accueil MVP-1.C avec wake-up backend), pas /office (3D V2).
      goto(localizeHref('/welcome'), { replaceState: true });
    }
  });

  // Maintenance du heartbeat session backend. Le sessionStore (singleton JS) survit aux
  // navigations SPA mais est detruit a chaque full page refresh. Sans cet effect au niveau
  // layout, un refresh sur /[map] (ou URL directe vers /[map]) laisserait la session backend
  // sans heartbeat front-side : a T+16min le control_plane shutdown la session croyant le
  // user parti. En declenchant sessionStore.start() ici, on garantit que toute route
  // authentifiee remet le heartbeat en marche apres un refresh.
  //
  // start() est idempotent backend-side (ADR 020 : 200 si session existe pour ce sub) et
  // fait un tick heartbeat immediat des l'arrivee en phase=ready, donc le cycle ne perd
  // jamais plus de quelques centaines de ms apres un refresh.
  //
  // Guard auth.loggingOut : pendant la fenetre logout (entre /stop et la navigation Cognito),
  // l'access_token reste valide cote serveur. Sans ce guard, un re-run reflex de l'effect
  // (race condition observable) re-call sessionStore.start() qui ressuscite une session EC2
  // silencieusement juste avant que le browser navigue ailleurs.
  $effect(() => {
    if (!auth.initialized) return;
    if (!auth.isAuthenticated) return;
    if (auth.loggingOut) return;
    // untrack() casse le tracking transitif des $state lus dans start() (notamment phase).
    // Sans cela, sessionStore.stop() qui ecrit phase='idle' re-queue ce $effect ; meme avec
    // beginLogout() en amont c'est defense-in-depth : si un futur appelant de stop oublie le
    // flag, ce guard local empeche le start parasite. Volonte : ce $effect re-run UNIQUEMENT
    // sur changement d'auth (login/logout/restore), jamais sur l'etat interne du sessionStore.
    untrack(() => {
      void sessionStore.start();
    });
  });

  // Path de-localise reactif : sert au check du UserMenu (cache sur /[map]).
  const dePath = $derived(deLocalizeUrl(page.url).pathname);
  const showUserMenu = $derived(
    auth.initialized && auth.isAuthenticated && !ROUTES_WITHOUT_USERMENU.includes(dePath)
  );
</script>

<svelte:head>
  <title>RMF Multi-Fleet Demo</title>
  <meta
    name="description"
    content="Visualisation 3D temps réel d'une démonstration Open-RMF avec deux flottes hétérogènes."
  />
</svelte:head>

{@render children()}
<!-- UserMenu global : visible sur welcome et toute future page authentifiee non-3D.
     Cache sur /[map] (3D demo) ou il chevauche le HUD existant. Wrappe dans son propre
     {#key currentLocale} pour qu'il se re-evalue au switch de langue meme si le children
     en cours n'a pas son propre {#key}. -->
{#key currentLocale}
  {#if showUserMenu}
    <UserMenu />
  {/if}
{/key}
